import { Skeleton } from '@/src/components/Antd';
import { message } from 'antd';
import Layout from '@/src/layout/EditorPageLayout/EditorPageLayout';
import TopLeft from '@/src/layout/EditorPageLayout/GoBackAndBreadCrumb/GoBackAndBreadCrumb';
import {
  getPlanDetails,
  getStoreRegion,
  handleStripeSession,
  handleLicenceCode,
  cancelPlan,
} from '@/src/request/settings/planAndBilling';
import {
  isUserValid,
  storeResourceNameGenerator,
} from '@/src/tools/getUserInfo';
import {
  EPermissionSource,
  EnumCategory,
  EnumOperation,
  EnumSubCategory,
} from '@/src/types/common/resources';
import React, { Fragment, useEffect, useState } from 'react';
import { useRequest } from 'ahooks';
import { useHistory } from 'react-router';
import { useI18n } from 'src/hooks/useRouterChange';
import { i18nGet } from 'src/tools/tools';
import UpdatePlanSection from './UpdatePlanSection';
import CurrentPlanSection from './CurrentPlanSection';
import { breadcrumbData } from './config';
import { CN_REGION, EPlanStatus, EPlanType } from '@/src/types/common';
import { get } from 'lodash';
import { getStoreUuid } from 'src/tools/getUserInfo';
import emit from 'src/tools/event';
import { UPDATE_STORE_SETTING } from 'src/tools/constant';
import css from './css.module.less';

const permissions = () => {
  const view_plan_setting = isUserValid({
    source: EPermissionSource.store,
    resourcesGroup: [
      storeResourceNameGenerator({
        category: EnumCategory.Settings,
        subCategory: EnumSubCategory.SettingsPlanBilling,
        operation: EnumOperation.view_plan,
      }),
    ],
  });
  const edit_plan_setting = isUserValid({
    source: EPermissionSource.store,
    resourcesGroup: [
      storeResourceNameGenerator({
        category: EnumCategory.Settings,
        subCategory: EnumSubCategory.SettingsPlanBilling,
        operation: EnumOperation.add_edit_plan,
      }),
    ],
  });
  return {
    view_plan_setting,
    edit_plan_setting,
  };
};

const PlanAndBilling = () => {
  const i18n = useI18n();
  const getI18n = (str) => i18nGet(i18n, str);
  const { view_plan_setting, edit_plan_setting } = permissions();
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [planData, setPlanData] = useState<any>({});
  const [storeRegion, setStoreRegion] = useState<string>('');

  const storeUuid = getStoreUuid();

  useEffect(() => {
    if (!view_plan_setting || !storeUuid) {
      setLoading(false);
      return;
    }

    setLoading(true);
    Promise.all([getPlanDetails(storeUuid), getStoreRegion(storeUuid)])
      .then(([planDetails, regionData]) => {
        setPlanData(planDetails || {});
        setStoreRegion(regionData?.code || '');
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, [view_plan_setting, storeUuid]);

  const { run: runManagePlan, loading: updateLoading } = useRequest(
    async (stripePriceId?: string | null) => {
      const session = await handleStripeSession(storeUuid, stripePriceId || '');
      if (stripePriceId && session?.checkoutSessionUrl) {
        window.location.href = session.checkoutSessionUrl;
      } else if (session?.url) {
        window.location.href = session.url;
      }
    },
    {
      manual: true,
      onError: () => {
        message.error('Failed to redirect to payment page');
      },
    },
  );

  // 处理管理计划（跳转到 Stripe 支付页面）
  const handleManagePlan = (stripePriceId?: string | null) => {
    runManagePlan(stripePriceId);
  };

  // 处理取消企业计划
  const handleCancelEnterprisePlan = async () => {
    try {
      await cancelPlan(storeUuid);
      message.success(getI18n('storePlanAndBilling.index.cancelledToast'));
      // 重新加载计划数据
      const planDetails = await getPlanDetails(storeUuid);
      setPlanData(planDetails || {});
      emit.emit(UPDATE_STORE_SETTING);
    } catch (error) {
      message.error('Failed to cancel plan');
    }
  };
  //enterpise submit
  const handleEnterpriseSubmit = async (code) => {
    try {
      const planDetails = await handleLicenceCode({
        licenceCode: code,
        storeUuid,
        planType: EPlanType.ENTERPRISE,
      });
      setPlanData((prev) => ({
        ...prev,
        ...planDetails,
        prices: prev?.prices || [],
      }));
      emit.emit(UPDATE_STORE_SETTING);
      message.success(getI18n('storePlanAndBilling.index.enterpriseToast'));
    } catch (error) {
      throw error;
    }
  };

  // details数据提取
  const planType = get(planData, 'planType') as EPlanType;
  const planStatus = get(planData, 'planStatus') as EPlanStatus;
  const activeDate = get(planData, 'activeDate', '');
  const expireDate = get(planData, 'expireDate', '');
  const nextBillingDate = get(planData, 'nextBillingDate', '');
  const prices = get(planData, 'prices', []);
  const isShowUpdateSection = !(
    storeRegion === CN_REGION && planStatus === EPlanStatus.ACTIVE
  );

  const goBackToList = () => {
    history.push({
      pathname: '/settings',
    });
  };

  const topLeft = (
    <TopLeft
      goBack={goBackToList}
      breadcrumbData={breadcrumbData({ getI18n })}
    />
  );

  const Child = [
    {
      child: view_plan_setting ? (
        <CurrentPlanSection
          planType={planType}
          planStatus={planStatus}
          activeDate={activeDate}
          expireDate={expireDate}
          nextBillingDate={nextBillingDate}
          storeRegion={storeRegion}
          prices={prices}
          disabled={!edit_plan_setting}
          updateLoading={updateLoading}
          handleManagePlan={handleManagePlan}
          handleCancelEnterprisePlan={handleCancelEnterprisePlan}
        />
      ) : null,
    },
    {
      child: view_plan_setting
        ? isShowUpdateSection && (
            <UpdatePlanSection
              planType={planType}
              planStatus={planStatus}
              storeRegion={storeRegion}
              prices={prices}
              handleUpdatePlan={handleManagePlan}
              handleEnterpriseSubmit={handleEnterpriseSubmit}
              disabled={!edit_plan_setting}
              updateLoading={updateLoading}
            />
          )
        : null,
    },
  ];

  return (
    <Fragment>
      <Skeleton loading={loading} active>
        <Layout
          elements={Child}
          topLeft={topLeft}
          layoutLeftClassName={css.layoutMinWidth}></Layout>
      </Skeleton>
    </Fragment>
  );
};

export default PlanAndBilling;
